import axios from "axios";

const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "";
const rawApiUrl = process.env.REACT_APP_BACKEND_URL || fallbackOrigin;
const API_URL = rawApiUrl.replace(/\/$/, "");

const TOKEN_KEY = "genz_access_token";
/** Fired when the session is gone for good. AuthContext listens. */
export const SESSION_EXPIRED_EVENT = "genz:session-expired";

const api = axios.create({
    baseURL: API_URL || undefined,
    withCredentials: true,
});

api.interceptors.request.use(config => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/** Endpoints where a 401 is a normal answer, not an expired session. */
const AUTH_PATHS = /\/api\/(auth\/(login|register|logout|refresh|forgot-password|reset-password)|oauth\/)/;

// One refresh at a time. Without this, a dashboard that fires six requests on
// mount would fire six refreshes the moment the access token expires.
let refreshPromise = null;

function clearSession() {
    try {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem("wizard_progress");
    } catch { /* private mode */ }
}

api.interceptors.response.use(
    response => response,
    async error => {
        const { response, config } = error;

        if (
            response?.status !== 401 ||
            !config ||
            config._retriedAfterRefresh ||
            AUTH_PATHS.test(config.url || "")
        ) {
            return Promise.reject(error);
        }

        // Anonymous visitors hitting a protected endpoint should just get the
        // 401; ProtectedRoute sends them to /login. Only try to refresh when we
        // actually believed we had a session.
        if (!localStorage.getItem(TOKEN_KEY)) {
            return Promise.reject(error);
        }

        try {
            // The access token lives 24h and the frontend never renewed it, so
            // sessions silently died mid-use: the navbar still showed the user
            // while every request failed with its own generic error message.
            refreshPromise = refreshPromise || api
                .post("/api/auth/refresh", null, { _retriedAfterRefresh: true })
                .finally(() => { refreshPromise = null; });

            const refreshed = await refreshPromise;
            const newToken = refreshed?.data?.access_token;
            if (newToken) localStorage.setItem(TOKEN_KEY, newToken);

            config._retriedAfterRefresh = true;
            if (newToken) config.headers.Authorization = `Bearer ${newToken}`;
            return api(config);
        } catch {
            clearSession();
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
            }
            return Promise.reject(error);
        }
    }
);

export default api;
export { API_URL, TOKEN_KEY };
